//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { AppBar, Button, CircularProgress, Toolbar, Typography, useTheme } from '@mui/material'

import Box from '@mui/material/Box'
import { RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import { PoolBaseOverviewCTO } from '@shared/sharedTypes'
import React, { ReactElement, useEffect } from 'react'

import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import AlertMenu from './AlertMenu'

import { logOT } from '@shared/loggingShared'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import AccountButtonMenu from './AccountButtonMenu'
import { ChainSelectorButtonMenu } from './ChainSelectorButtonMenu'

import SyncIcon from '@mui/icons-material/Sync'
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled'

import { DirectConnectionMenu } from './DirectConnectionMenu'
import axios from 'axios'
import ConnectButtonAppKit from './ConnectButtonAppKit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'

export function TopAppBar(): React.ReactElement {
  const theme = useTheme()
  const appLocation = useLocation()
  const {
    stageConfiguration,
    chainConfiguration,
    userConnection: { roleEnum, accountAddr },
    wsIsConnected,
    isLoggedIn,
    isLoggingIn,
    webSocket,
  } = useOtChainState()

  // const { roleEnum, isLoggedIn, signerKey, wsIsConnected } = useOtChainStateUserConnection()
  const chainTimestamp = useOtChainTimestamp()

  const navigate = useNavigate()

  const [lateAccrualData, setLateAccrualData] = React.useState<
    { address: EthAddr; url?: string; borrowerAddress: EthAddr }[]
  >([])
  const [processingConnection, setProcessingConnection] = React.useState<boolean>(false)
  const [misconfigurationMessage, _setMisconfigurationMessage] = React.useState<string | undefined>(undefined)
  const [isLateAccrualVisible, setIsLateAccrualVisible] = React.useState<boolean>(true)
  const [isLateAccrualExpanded, setIsLateAccrualExpanded] = React.useState<boolean>(false)

  useEffect(() => {
    async function fetchLateAccrual() {
      if (roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) {
        try {
          const response = await axios.get(`${stageConfiguration.serverApiBaseUrl}/lateAccruals`, {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': stageConfiguration.restAPIKey,
            },
          })

          // Only store addresses of pools that are late for accrual
          if (response.data.issues) {
            logOT.info('Late accrual data:', response.data.issues)
            const filteredIssues =
              roleEnum === RoleEnum.BORROWER
                ? response.data.issues.filter(
                    (issue: { borrowerAddress: string }) =>
                      issue.borrowerAddress.toLowerCase() === accountAddr?.toLowerCase()
                  )
                : response.data.issues

            const latePoolAddresses = filteredIssues
              .filter((issue: { poolAddr: string; late?: boolean }) => issue.late)
              .map((issue: { poolAddr: string; blockExplorerUrl: string; borrowerAddress: string }) => ({
                address: issue.poolAddr as EthAddr,
                borrowerAddress: issue.borrowerAddress as EthAddr,
                url: issue.blockExplorerUrl,
              }))
            logOT.info('2.Late accrual data:', latePoolAddresses)
            setLateAccrualData(latePoolAddresses)
          } else {
            setLateAccrualData([])
          }
        } catch (error) {
          logOT.error('Error fetching late accrual data:', error)
          setLateAccrualData([])
        }
      }
    }

    fetchLateAccrual()
  }, [roleEnum, stageConfiguration, accountAddr, roleEnum])
  const useDirectConnect = (stageConfiguration.userConnections?.length ?? 0) > 0

  let poolAddr: EthAddr | undefined
  if (appLocation.pathname.startsWith('/pools/')) {
    poolAddr = appLocation.pathname.split('/')[2]
  }

  const { poolOverviewCTOs, isInitialized } = useOtSelector((state) => state.poolState)

  const poolBaseOverviewCTO = poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolBaseOverviewCTO

  const roleColor =
    roleEnum === RoleEnum.BORROWER
      ? theme.palette.accent2.subtle
      : roleEnum === RoleEnum.LENDER
        ? theme.palette.accent1.subtle
        : theme.palette.accent3.subtle

  useEffect(() => {
    if (isLoggedIn) {
      const event = new Event('modalClose')
      window.dispatchEvent(event)
      setProcessingConnection(false)
    }
  }, [isLoggedIn, processingConnection])

  const sxSelectedTab = {
    color: theme.palette.fg.main,
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '27px',
    textTransform: 'none',
  }
  const sxUnselectedTab = {
    color: '#616161',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '27px',
    textTransform: 'none',
  }

  const investmentsSelected =
    appLocation.pathname === '/investments' ||
    (appLocation.pathname.startsWith('/pools/') &&
      poolAddr &&
      Number(poolBaseOverviewCTO?.totalOutstandingLoanPrincipal) > 10)
  const availableSelected =
    appLocation.pathname === '/availableVaults' ||
    (appLocation.pathname.startsWith('/pools/') &&
      poolAddr &&
      Number(poolBaseOverviewCTO?.totalOutstandingLoanPrincipal) < 10)

  let connectButton: ReactElement = <></>

  if (isLoggingIn) {
    connectButton = (
      <Box>
        <CircularProgress />
      </Box>
    )
  } else if (
    !useDirectConnect &&
    isInitialized &&
    !isLoggedIn &&
    chainConfiguration &&
    (stageConfiguration.isLyt || webSocket)
  ) {
    connectButton = (
      <Box>
        <ConnectButtonAppKit />
      </Box>
    )
  } else if (useDirectConnect && chainConfiguration && (stageConfiguration.isLyt || webSocket)) {
    connectButton = (
      <Box>
        <DirectConnectionMenu></DirectConnectionMenu>
      </Box>
    )
  }
  // @ts-ignore

  // const accountAddrDisplay = chainContext?.accountAddr ? ethValueDisplay(chainContext.accountAddr) : ''
  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ flexGrow: 1 }} elevation={0}>
        <Toolbar
          sx={{
            padding: '24px',
            flexGrow: 1,
            backgroundColor: theme.palette.background.default,
            color: 'black',
          }}
        >
          {/* <button className={styles.menuBtn} onClick={toggleOpen}> */}
          {/*  {open ? <KeyboardDoubleArrowLeftIcon/> : <KeyboardDoubleArrowRightIcon/>} */}
          {/* </button> */}
          <Box
            sx={{
              alignContent: 'flex-start',
            }}
            onClick={() => {
              // Removed version checking - content-based cache busting handles cache invalidation automatically
              logOT.debug('location navigate to /')
              navigate('/')
            }}
          >
            <img src="/OpenTrade.png" alt="logo" style={{ maxHeight: 40 }} />
          </Box>
          <Box sx={{ alignContent: 'center', flexGrow: 1 }}>
            {misconfigurationMessage && (
              <Typography variant={'h1'} sx={{ color: 'red' }}>
                {misconfigurationMessage}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              paddingRight: '8px',
              alignContent: 'flex-end',
              marginRight: 4,
            }}
          ></Box>
          <Box
            sx={{
              paddingRight: '8px',
              alignContent: 'flex-end',
              marginRight: 4,
            }}
          >
            <AlertMenu />
          </Box>

          <ChainSelectorButtonMenu></ChainSelectorButtonMenu>
          {wsIsConnected ? <SyncIcon /> : <SyncDisabledIcon />}
          <Box sx={{ alignContent: 'flex-end' }}>{connectButton}</Box>
          <Box sx={{ zIndex: 1000 }}>{accountAddr && <AccountButtonMenu></AccountButtonMenu>}</Box>
        </Toolbar>
        <Toolbar sx={{ flexGrow: 1, backgroundColor: roleColor, color: 'black' }}>
          {roleEnum === RoleEnum.LENDER && (
            <Box sx={{ backgroundColor: roleColor, alignContent: 'flex-start' }}>
              <Button sx={appLocation.pathname === '/' ? sxSelectedTab : sxUnselectedTab} component={Link} to={'/'}>
                Home
              </Button>
              <Button sx={investmentsSelected ? sxSelectedTab : sxUnselectedTab} component={Link} to={'/investments'}>
                My Investments
              </Button>
              <Button sx={availableSelected ? sxSelectedTab : sxUnselectedTab} component={Link} to={'/availableVaults'}>
                Active Loan Vaults
              </Button>
              <Button
                sx={appLocation.pathname === '/transactions' ? sxSelectedTab : sxUnselectedTab}
                component={Link}
                to={'/transactions'}
              >
                Transactions
              </Button>
              <Button
                sx={appLocation.pathname === '/reporting' ? sxSelectedTab : sxUnselectedTab}
                component={Link}
                to={'/reporting'}
              >
                Reports
              </Button>
            </Box>
          )}
          <Box sx={{ flexGrow: 1, alignContent: 'center' }}></Box>
          <Box sx={{ alignContent: 'flex-end' }}>
            {chainTimestamp && (
              <Typography variant={'small'}>
                {new Date(chainTimestamp * 1000).toUTCString().replace('GMT', 'UTC')}
              </Typography>
            )}
          </Box>
        </Toolbar>
        {isLateAccrualVisible && lateAccrualData.length > 0 && (
          <Toolbar
            sx={{
              backgroundColor: '#fff3e0',
              color: 'black',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              minHeight: '50px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                cursor: 'pointer',
              }}
              onClick={() => setIsLateAccrualVisible(false)}
            >
              <CloseIcon sx={{ color: '#ed6c02' }} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                marginTop: 2,
                marginBottom: isLateAccrualExpanded ? 1 : 0,
              }}
              onClick={() => setIsLateAccrualExpanded(!isLateAccrualExpanded)}
            >
              <Typography sx={{ color: '#ed6c02', fontWeight: 'bold' }}>
                {isLateAccrualExpanded ? 'Hide Late Accruals' : 'Show Late Accruals'}
              </Typography>
              {isLateAccrualExpanded ? (
                <ExpandLessIcon sx={{ color: '#ed6c02', marginLeft: 1 }} />
              ) : (
                <ExpandMoreIcon sx={{ color: '#ed6c02', marginLeft: 1 }} />
              )}
            </Box>
            {isLateAccrualExpanded &&
              lateAccrualData.map((pool, index) => (
                <Typography key={index} sx={{ color: '#ed6c02', py: 0.5 }}>
                  Warning: Pool{' '}
                  <a
                    href={pool?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#ed6c02', textDecoration: 'underline' }}
                  >
                    {pool.address}
                  </a>{' '}
                  is late for accrual
                </Typography>
              ))}
          </Toolbar>
        )}
      </AppBar>
    </Box>
  )
}
